<template>
  <v-card
    v-if="props.tasks.length === 0"
    class="mt-3 mb-3"
  >
    <v-card-title>
      No tasks found.
    </v-card-title>
    <v-card-text>
      There are no tasks currently assigned to you.
    </v-card-text>
  </v-card>
  <VList
    v-if="props.tasks.length > 0"
    lines
    border
    rounded
  >
    <template
      v-for="(item, index) of props.tasks"
      :key="item.id"
    >
      <router-link :to="{ name: 'task-id', params: { id: item.id } }">
        <VListItem :class="acStatusRowClass(item)">
          <!-- LOGO |      Screen MD and bigger-->
          <template
            v-if="$vuetify.display.mdAndUp"
            #prepend
          >
            <VAvatar
              v-if="item.Flight"
              :image="getLogo(item.Flight.PrefixIATA)"
              rounded="0"
              size="50"
            />
            <VAvatar
              v-else
              :image="getLogo('nayak')"
              rounded="0"
              size="50"
            />
          </template>
          <!--        Smaller then MD-->
          <template
            v-else
            #prepend
          >
            <VAvatar
              v-if="item.Flight"
              :image="getLogo(item.Flight.PrefixIATA)"
              rounded="0"
            />
            <VAvatar
              v-else
              :image="getLogo('nayak')"
              rounded="0"
              size="50"
            />
          </template>
          <!-- LOGO | end screen-size-->

          <VListItemTitle class="overflow-visible">
            {{ item.Description }}
            <v-chip
              v-if="isWorkRegistrationInProgress(item)"
              color="warning"
            >
              Work registration in progress
            </v-chip>
            <v-chip
              v-if="isWorkorderCreated(item)"
              color="success"
            >
              Workorder issued
            </v-chip>
            <!-- DEVAMS-2070: amber = issued/awaiting note, green = all complete -->
            <!-- pt-2 pr-2 gives the floating badge counter room above/right of the icon -->
            <router-link
              v-if="item.handoverCount > 0"
              :to="{ name: 'task-id-aircraft-handover', params: { id: item.id } }"
              class="d-inline-block pt-2 pr-2 ml-1"
              @click.stop
            >
              <v-badge
                :content="item.handoverCount"
                :color="item.hasOpenHandover ? 'amber-darken-2' : 'success'"
              >
                <v-icon
                  :color="item.hasOpenHandover ? 'amber-darken-2' : 'success'"
                  :title="item.hasOpenHandover ? 'Handover awaiting acceptance' : 'All handovers complete'"
                >
                  {{ item.hasOpenHandover ? 'mdi-clipboard-alert-outline' : 'mdi-clipboard-check-outline' }}
                </v-icon>
              </v-badge>
            </router-link>
          </VListItemTitle>
          <VListItemSubtitle
            v-if="$vuetify.display.smAndUp"
            class="mt-1 "
          >
            <span class="text-disabled ml-4">
              <template
                v-for="(user, index) of generateUserDetails(item)"
                :key="index"
              >
                <VBadge
                  dot
                  location="start top"
                  :color="stateToColor(user.state)"
                  offset-x="-9"
                  offset-y="-1"
                  class="mr-3"
                >
                  {{ user.username }},
                </VBadge>
              </template>
            </span>
          </VListItemSubtitle>
          <VListItemSubtitle class="mt-2">
            <div class="ml-2">
              <VBadge
                v-if="item.Flight"
                dot
                location="start center"
                :color="getStateInfo(item.Flight.State)"
              >
                <span
                  v-if="!showDeparted"
                  class="ms-4"
                >{{ getStateInfo(item.Flight.State, false) }}</span>
                <span
                  v-if="showDeparted"
                  class="ms-4"
                >Departed</span>
              </VBadge>
            </div>
          </VListItemSubtitle>

          <!-- Additional info (RH screen)   bigger screens-->
          <template
            v-if="$vuetify.display.mdAndUp"
            #append
          >
            <div
              v-if="item.Flight"
              class="flex-column right-col"
            >
              <VListItemTitle>
                <a
                  target="_blank"
                  :href="'https://www.flightradar24.com/' + item.Flight.AircraftRegistration"
                  @click.stop
                >
                  <VBadge
                    :content="item.Flight.AircraftType"
                    :offset-x="-19"
                    :offset-y="6"
                    color="info"
                  >
                    {{ item.Flight.AircraftRegistration || 'UNK' }}
                  </VBadge>
                </a>
              </VListItemTitle>
              <VListItemSubtitle class="mt-1 gate-position-indicator">
                <span class="gate-number">
                  {{ item.Flight.Ramp || 'UNK' }}
                  <template v-if="item.Flight.ConnectingFlight && item.Flight.ConnectingFlight.Ramp && item.Flight.ConnectingFlight.Ramp !== item.Flight.Ramp">
                    &rarr; {{ item.Flight.ConnectingFlight.Ramp }}
                  </template>
                  <v-tooltip
                    v-if="item.Flight.rampEnriched"
                    text="Stand from MXP ground data"
                    location="top"
                  >
                    <template #activator="{ props: tooltipProps }">
                      <v-icon
                        v-bind="tooltipProps"
                        size="12"
                        color="info"
                        class="ml-1"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                  </v-tooltip>
                </span>
              </VListItemSubtitle>
            </div>
            <div class="flex-column right-col">
              <VListItemTitle>
                {{ displayStartTime(item) }}
              </VListItemTitle>
              <VListItemSubtitle class="mt-1">
                <span class="text-disabled">{{ getDepartureDisplayTime(item) }}</span>
              </VListItemSubtitle>
            </div>
          </template>

          <!-- smaller screens-->
          <template
            v-else
            #append
          >
            <div
              v-if="item.Flight"
              class="flex-column"
            >
              <VListItemTitle>
                <VBadge
                  :content="item.Flight.Ramp"
                  inline
                  color="primary"
                >
                  {{ item.Flight.AircraftRegistration || 'UNK' }}&nbsp;
                </VBadge>
              </VListItemTitle>
              <VListItemSubtitle class="mt-1">
                {{ displayStartTime(item) }}
              </VListItemSubtitle>
            </div>
          </template>
        <!--Additional info (RH screen)   end screen-size-->
        </VListItem>
      </router-link>
      <VDivider v-if="index !== tasks.length - 1" />
    </template>
  </VList>
</template>

<script setup>
import {parseFlightState, stringtoDateTime, stateToColor, generateUserDetails} from "@/plugins/helpers";
import {useRouter} from 'vue-router';
import logos from "@/plugins/logos";

const props = defineProps({
  tasks: Object,
  showDeparted: Boolean,
})
const router = useRouter();

/**
 * Determines whether there is a work registration in progress by checking
 * if any workorder within the provided item has a null `AMOSWorkorder` value.
 *
 * The function iterates through the `workorder` array within the given item
 * and returns `true` if at least one workorder has a `null` `AMOSWorkorder`.
 * Returns `false` otherwise.
 *
 * @param {Object} item - The object containing the workorder data to evaluate.
 * @returns {boolean} - `true` if a work registration is in progress, `false` otherwise.
 */
const isWorkRegistrationInProgress = ((item) => {
  if (item && item.workorder && Array.isArray(item.workorder)) {
    for (const workOrder of item.workorder) {
      if (workOrder.AMOSWorkorder === null) {
        return true; // Found a null AMOSWorkorder
      }
    }
  }
  return false;
})
/**
 * Determines if a work order is created based on a provided item object.
 *
 * This variable checks the `workorder` property of the given `item` object. It validates
 * if `workorder` exists, is an array, and contains non-null `AMOSWorkorder` values.
 *
 * @param {Object} item - The item object to evaluate.
 * @param {Array} item.workorder - The work order array associated with the item.
 * @returns {boolean} Returns `true` if the `workorder` array exists, is non-empty,
 * and all `AMOSWorkorder` values in the array are non-null. Returns `false` otherwise.
 */
const isWorkorderCreated = ((item) => {
  if (item && item.workorder && Array.isArray(item.workorder)) {
    if (item.workorder.length === 0) {
      return false; // workorder is an empty array
    }
    for (const workOrder of item.workorder) {
      if (workOrder.AMOSWorkorder === null) {
        return false; // Found a null AMOSWorkorder
      }
    }
    return true; // All AMOSWorkorder values are non-null and the array is not empty
  }
  return false; // workorder is null, undefined, or not an array
})

function getStateInfo(state, nameOrColor = true) {
  const stateObject = parseFlightState(state);
  if (nameOrColor === true) {
    return stateObject.color;
  }
  return stateObject.name;
}

function getLogo(iata) {
  const logoSrc = 'nayak.png';
  if (logos[iata + '.png']) {
    return logos[iata + '.png'];
  }
  return logos[logoSrc];
}

function openTask(id) {
  router.push({name: 'task-id', params: {id: id}});
}

const displayStartTime = (item) => {
  const flight = item.Flight;
  if (flight) {
    if (flight.ActualInBlockTime) {
      return stringtoDateTime(flight.ActualInBlockTime, 'd MMM HH:mm');
    }
    if (flight.EstimatedInBlockTime) {
      return stringtoDateTime(flight.EstimatedInBlockTime, 'd MMM HH:mm');
    }
    if (flight.ScheduledInBlockTime) {
      return stringtoDateTime(flight.ScheduledInBlockTime, 'd MMM HH:mm');
    }
    if (flight.ScheduleTime) {
      return stringtoDateTime(flight.ScheduleTime, 'd MMM HH:mm');
    }
  }
  // Fallback to item.Start if all flight times are null or Flight object is missing
  return stringtoDateTime(item.Start, 'd MMM HH:mm')+'(sch)';
};

function acStatusRowClass(item) {
  if (item.acStatus === 'aog') return 'row-aog'
  if (item.acStatus === 'critical') return 'row-critical'
  return ''
}

const getDepartureDisplayTime = (item) => {
  const connectingFlight = item.Flight?.ConnectingFlight; // Use optional chaining for safety

  if (connectingFlight) {
    if (connectingFlight.ActualOffBlockTime) {
      return stringtoDateTime(connectingFlight.ActualOffBlockTime, 'd MMM HH:mm');
    }
    if (connectingFlight.EstimatedOffBlockTime) {
      return stringtoDateTime(connectingFlight.EstimatedOffBlockTime, 'd MMM HH:mm');
    }
    if (connectingFlight.ScheduledOffBlockTime) {
      return stringtoDateTime(connectingFlight.ScheduledOffBlockTime, 'd MMM HH:mm');
    }
    // Note: The 'ScheduleTime' for the connecting flight might be at the top level of ConnectingFlight
    if (connectingFlight.ScheduleTime) {
      return stringtoDateTime(connectingFlight.ScheduleTime, 'd MMM HH:mm');
    }
  }

  // Fallback to item.End if no connecting flight or no specific departure times are found
  return stringtoDateTime(item.End, 'd MMM HH:mm');
};
</script>

<style scoped lang="scss">

.right-col {
  margin-left: auto;
  margin-right: 0;
  width: 119px;
  text-align: left;
}

.row-aog {
  background-color: rgba(244, 67, 54, 0.1);
}

.row-critical {
  background-color: rgba(255, 235, 59, 0.15);
}
</style>
